import { TextField, createTheme, styled, type ThemeOptions } from "@mui/material";
import { tokens, fontSize, fontWeight } from "./tokens";

// Extend Material-UI theme to include custom typography variants
declare module "@mui/material/styles" {
  interface TypographyVariants {
    title: React.CSSProperties;
    label: React.CSSProperties;
    description: React.CSSProperties;
    header: React.CSSProperties;
    modal_description: React.CSSProperties;
    name: React.CSSProperties;
    badge: React.CSSProperties;
    breadcrumbs: React.CSSProperties;
    weak: React.CSSProperties;
    table_head: React.CSSProperties;
    button: React.CSSProperties;
    nav: React.CSSProperties;
    section: React.CSSProperties;
  }

  // Allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    title?: React.CSSProperties;
    label?: React.CSSProperties;
    description?: React.CSSProperties;
    header?: React.CSSProperties;
    modal_description?: React.CSSProperties;
    name?: React.CSSProperties;
    badge?: React.CSSProperties;
    breadcrumbs?: React.CSSProperties;
    weak?: React.CSSProperties;
    table_head?: React.CSSProperties;
    button?: React.CSSProperties;
    nav?: React.CSSProperties;
    section?: React.CSSProperties;
  }

  interface Palette {
    yudu: Palette["primary"];
  }

  interface PaletteOptions {
    yudu?: PaletteOptions["primary"];
  }
}

// Extend the Typography prop to include custom variants
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    title: true;
    label: true;
    description: true;
    icon: true;
    header: true;
    modal_description: true;
    name: true;
    badge: true;
    body3: true;
    body4: true;
    breadcrumbs: true;
    weak: true;
    table_head: true;
    button: true;
    nav: true;
    section: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    yudu: true;
  }
}

// Define the theme
const darkPalette = {
  dividerChannel: "241 241 241",
  background: {
    paper: tokens.paperDark,
    paperChannel: "red",
  },
  contrastThreshold: 4.5,
  primary: {
    main: tokens.primary,
    contrastText: tokens.white,
  },
  secondary: {
    main: tokens.secondary,
    contrastText: tokens.white,
  },
  success: {
    main: tokens.success,
    contrastText: tokens.white,
  },
  error: {
    main: tokens.errorMaterial,
    contrastText: tokens.white,
  },
  warning: {
    main: tokens.warning,
  },
  info: {
    main: tokens.infoMaterial,
  },
  yudu: {
    main: tokens.secondary,
    contrastText: tokens.white,
  },
};

const lightPalette = {
  dividerChannel: "241 241 241",
  background: {
    default: tokens.bgLight,
  },
  primary: {
    main: tokens.primary,
    contrastText: tokens.white,
  },
  secondary: {
    main: tokens.secondary,
    contrastText: tokens.white,
  },
  success: {
    main: tokens.success,
  },
  error: {
    main: tokens.errorMaterial,
  },
  warning: {
    main: tokens.warning,
  },
  info: {
    main: tokens.infoMaterial,
  },
  yudu: {
    main: tokens.secondary,
    contrastText: tokens.white,
  },
};

const sharedThemeOptions = {
  typography: {
    fontSize: 14,
    fontFamily: "inter",
    title: {
      fontSize: fontSize["3xl"],
      fontWeight: fontWeight.bold,
      letterSpacing: "0.75px",
    },
    breadcrumbs: {
      fontSize: fontSize.md,
      fontWeight: fontWeight.medium,
      letterSpacing: "0px",
      opacity: 0.5,
    },
    label: {
      fontSize: fontSize.md,
      fontWeight: fontWeight.medium,
      color: tokens.grayText,
      letterSpacing: "0.25px",
    },
    description: {
      fontSize: fontSize.sm,
      fontWeight: fontWeight.regular,
      color: tokens.textDescription,
    },
    header: {
      fontSize: fontSize["2xl"],
      fontWeight: fontWeight.bold,
      letterSpacing: "0.75px",
    },
    subtitle1: {
      fontSize: fontSize.lg,
      fontWeight: fontWeight.regular,
      letterSpacing: "0.25px",
      opacity: 0.6,
    },
    subtitle2: {
      fontSize: fontSize.md,
      fontWeight: fontWeight.extrabold,
      letterSpacing: "0.5px",
    },
    body1: {
      fontSize: fontSize.md,
      fontWeight: fontWeight.medium,
      letterSpacing: "0.75px",
    },
    body2: {
      fontSize: fontSize.sm,
      fontWeight: fontWeight.medium,
      letterSpacing: "0.75px",
    },
    weak: {
      fontSize: fontSize.base,
      opacity: 0.4,
    },
    table_head: {
      fontSize: fontSize.base,
      fontWeight: fontWeight.regular,
      letterSpacing: "0.5px",
      opacity: 0.5,
    },
    badge: {
      fontSize: fontSize.xs,
      fontWeight: fontWeight.bold,
      letterSpacing: "0.75px",
      padding: "0.215rem 1rem",
      borderRadius: "1rem",
    },
    nav: {
      fontSize: fontSize.xl,
      fontWeight: fontWeight.bold,
      letterSpacing: "0.5px",
    },
    section: {
      fontSize: fontSize.lg,
      fontWeight: fontWeight.semibold,
      letterSpacing: "0.75px",
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1440,
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        // Render the custom title-like variants as real heading elements so
        // page titles expose role="heading" (a11y + e2e getByRole('heading')).
        // Visual styling is unchanged — MUI applies the variant's typography
        // styles regardless of the rendered element, and Typography defaults to
        // margin:0 so layout is unaffected.
        variantMapping: {
          title: "h2",
          header: "h2",
        },
      },
    },
    // MuiTooltip: {
    //   styleOverrides: {
    //     tooltip: {
    //       fontSize: "8px",
    //     },
    //   },
    // },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 42,
          height: 26,
          padding: 0,
        },
        switchBase: {
          padding: 0,
          margin: 2,
          transitionDuration: "300ms",
          "&.Mui-checked": {
            transform: "translateX(16px)",
            color: tokens.white,
            "& + .MuiSwitch-track": {
              // backgroundColor: "#65C466",
              opacity: 1,
              border: 0,
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.5,
            },
          },
          "&.Mui-focusVisible .MuiSwitch-thumb": {
            // color: "#33cf4d",
            border: `6px solid ${tokens.white}`,
          },
          "&.Mui-disabled .MuiSwitch-thumb": {
            color: tokens.grayTrack,
          },
          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.7,
          },
        },
        thumb: {
          width: 22,
          height: 22,
          boxSizing: "border-box",
        },
        track: {
          borderRadius: 26 / 2,
          backgroundColor: tokens.grayTrack,
          opacity: 1,
          transition: "background-color 500ms",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
            {
              display: "none", // Hides spinner for Chrome/Safari
            },
          "& input[type=number]": {
            MozAppearance: "textfield", // Hides spinner for Firefox
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        color: "primary",
        variant: "contained",
      },
      styleOverrides: {
        root: {
          borderRadius: "8px",
          // textTransform: "capitalize",
          // "&.Mui-disabled": {
          //   backgroundColor: "#F7F7FC",
          //   color: "#F7F7FC",
          // },
        },
        contained: {
          "&.Mui-disabled": {
            backgroundColor: "var(--variant-containedBg)",
            color: "var(--variant-containedColor)",
            opacity: 0.5,
          },
        },
        outlined: {
          "&.Mui-disabled": {
            borderColor: "var(--variant-outlinedBorder)",
            color: "var(--variant-outlinedColor)",
            opacity: 0.5,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
            color: tokens.grayText,
          },
        },
        input: {
          "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            appearance: "none",
            margin: 0,
          },
          "&[type=number]": {
            MozAppearance: "textfield", // Firefox
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        invisible: {
          backgroundColor: "transparent",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundImage: "none",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundImage: "none",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small",
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiCard: {
      defaultProps: {
        style: {
          borderRadius: "12px",
        },
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
      },
    },
    MuiCardContent: {
      defaultProps:{
        style: {
          padding: '16px 24px',
        }
      }
    },
    MuiCardActions: {
      defaultProps:{
        style: {
          padding: '16px 24px',
        }
      }
    },
    MuiDialogActions: {
      defaultProps:{
        style: {
          padding: '16px 24px',
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '& td:first-of-type, & th:first-of-type': {
            paddingLeft: '24px !important',
          }
        }
      }
    },
    MuiSkeleton: {
      defaultProps: {
        animation: "wave",
      },
      styleOverrides: {
        rounded: {
          borderRadius: '12px'
        },
        root: ({theme}) => ({
          backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.21)" : "rgba(0,0,0,0.11)",
        })
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: "8px !important",
        }
      }
    }
  },
} satisfies ThemeOptions;

export const theme = createTheme({
  colorSchemes: {
    dark: { palette: darkPalette },
    light: { palette: lightPalette },
  },
  ...sharedThemeOptions,
});

export const lightOnlyTheme = createTheme({
  ...sharedThemeOptions,
  palette: { ...lightPalette, mode: "light" },
});

// Styled TextField. Explicit type annotation suppresses TS2742 (inferred type
// cannot be named without a reference to @mui/system internals).
export const SearchBox: typeof TextField = styled(TextField)(() => ({
  "& fieldset": {
    borderRadius: "8px",
  },
  input: {
    padding: "12px 0",
    WebkitBoxShadow: "0 0 0 1000px transparent inset",
  },
})) as typeof TextField;
